import { AudioEngineRemoteAudio } from '../../RoomContext'
import { AudioTrackWithInfo, AudioInputStreamInfo, AudioSourceInfo, AudioOutputSettings } from '../../UserContext'
import { AudioEngineConnectionState, AudioEngineError, AudioEngineRemoteStreamStats } from './index'
import FakeMediaStreamTrack from './FakeMediaStreamTrack'
import { AudioEngineJoinRoomParams, IAudioEngineRoom } from './IAudioEngineRoom'
import { ServerLatencyAnnotator } from '../../services/SfuNodeSelection'
import { AEStream, ReceiverStats, SampleFormat, StreamStats } from './WebAudioEngine/types'
import ElectronIPC from '../../services/ElectronIPC'
import { logger } from '../../services/Logger'
import { patcher } from './WebAudioPatcher'
import { AudioPatch } from './types'

const log = logger.createChildLogger({ name: 'WebAudioEngine' })

type StreamGraph = {
    workletNode: AudioWorkletNode
    recordingDestNode: MediaStreamAudioDestinationNode | undefined
    recordingSourceNode: MediaStreamAudioSourceNode | undefined
    patchDestNode: MediaStreamAudioDestinationNode | undefined
    recordingContext: AudioContext | undefined
}

type StreamHealthStats = {
    streamId: string
    latePacketCt: number
    outOfOrderPacketCt: number
    duplicatePacketCt: number
    totalOverflowCt: number
    totalUnderflowCt: number
    timestamp: number
}

function makeStreamGraph(workletNode: AudioWorkletNode): StreamGraph {
    return {
        workletNode,
        recordingDestNode: undefined,
        recordingSourceNode: undefined,
        recordingContext: undefined,
        patchDestNode: undefined,
    }
}

export class WebAudioEngineRoom implements IAudioEngineRoom {
    private receiverWorker: Worker
    private audioContext: AudioContext
    private masterDestNode: MediaStreamAudioDestinationNode
    private remoteStreams: Map<string, AudioEngineRemoteAudio> = new Map()
    private streamNodes: Map<string, StreamGraph> = new Map()
    private currentConnectionState: AudioEngineConnectionState = 'INITIAL'
    private streamHealthStats: Map<string, StreamHealthStats> = new Map()
    private currentReceiverStats: Map<string, ReceiverStats> = new Map()
    private receiverMessageHandler: (ev: MessageEvent) => void
    private workletInitialized: Promise<boolean>

    constructor(
        receiver: Worker,
        context: AudioContext,
        master: MediaStreamAudioDestinationNode,
        initialized: Promise<boolean>
    ) {
        this.receiverWorker = receiver
        this.audioContext = context
        this.masterDestNode = master
        this.workletInitialized = initialized

        this.receiverMessageHandler = (ev: MessageEvent) => {
            if (ev.data.type === 'connectionStateChange') {
                this.currentConnectionState = ev.data.state
            } else if (ev.data.type === 'stats') {
                this.currentReceiverStats = ev.data.stats
            }
        }
        this.receiverWorker.addEventListener('message', this.receiverMessageHandler)
    }

    async joinRoom(data: AudioEngineJoinRoomParams): Promise<string | AudioEngineError> {
        const relayServers = data.servers.map((server) => ({ url: server.websocketUrl, code: server.code }))
        const latencyAnnotator = new ServerLatencyAnnotator<{ url: string; code: string }>('/status')
        log.verbose('WebAudioEngineRoom', `finding nearest relay server from ${JSON.stringify(data.servers)}`)
        const nearest = await latencyAnnotator.setAvailableNodes(relayServers)
        log.verbose('WebAudioEngineRoom', `nearest server is ${nearest.url}`)

        this.receiverWorker.postMessage({
            type: 'joinRoom',
            payload: {
                opts: {
                    roomUrn: data.roomUrn,
                    authToken: data.userInfo.authToken,
                    userUrn: data.userInfo.userUrn,
                    username: data.userInfo.username,
                    defaultUrl: nearest.url,
                },
            },
        })

        const waitForResponse = (): Promise<string | AudioEngineError> => {
            return new Promise((r) => {
                const h = (ev: MessageEvent) => {
                    if (ev.data.type === 'joinRoomSuccess') {
                        r(ev.data.resolvedWebsocketUrl)
                        this.receiverWorker.removeEventListener('message', h)
                    } else if (ev.data.type === 'joinRoomFailed') {
                        r(new AudioEngineError('join_room_failed', 'Could not establish connection to relay'))
                        this.receiverWorker.removeEventListener('message', h)
                    }
                }
                this.receiverWorker.addEventListener('message', h)
            })
        }

        return waitForResponse()
    }

    async exitRoom(): Promise<boolean> {
        this.receiverWorker.postMessage({ type: 'disconnect' })
        return true
    }

    sendLocalStream(_track: AudioTrackWithInfo): Promise<boolean> {
        throw new Error('Web Audio Engine does not support sending audio.')
    }

    async receiveRemoteStream(
        streamUuid: string,
        info: AudioInputStreamInfo
    ): Promise<AudioEngineError | AudioEngineRemoteAudio> {
        await this.workletInitialized

        log.verbose('WebAudioEngineRoom/receiveRemoteStream', `Subscribing to stream ${streamUuid}`)

        if (this.remoteStreams.has(streamUuid)) {
            log.verbose('WebAudioEngineRoom/receiveRemoteStream', `Already subscribed to stream ${streamUuid}`)
            return this.remoteStreams.get(streamUuid)!
        }

        let sampleFormat: SampleFormat
        if (info.isCompressed) {
            sampleFormat = 'opus'
        } else if (info.bitDepth == 16) {
            sampleFormat = 's16le'
        } else if (info.bitDepth == 24) {
            sampleFormat = 's24le'
        } else if (info.bitDepth == 32) {
            sampleFormat = 'f32le'
        } else {
            throw new Error(`Unsupported bitdepth: ${info.bitDepth}`)
        }

        this.audioContext.resume()

        const stream: AEStream = {
            streamId: streamUuid,
            sampleFormat,
            sampleRate: info.sampleRate,
            numChannels: info.channelCount,
            bufferSizeMs: info.receiverBufferSizeMs,
            networkPacketDurationMs: info.networkPacketDurationMs ? info.networkPacketDurationMs : 20,
            channelSelection: info.channelSelection,
            channelUnpackingMap: info.channelUnpackingMap,
        }

        const outputChannels = Math.max(...info.channelSelection.map((cg) => cg.length))
        const workletNode = new AudioWorkletNode(this.audioContext, 'audio-processor', {
            outputChannelCount: [outputChannels],
        })
        workletNode.onprocessorerror = (ev: Event) => {
            log.error('WebAudioEngineRoom', 'worklet processor error', { ev })
        }
        workletNode.connect(this.audioContext.destination)
        workletNode.connect(this.masterDestNode)

        const { port1: workerPort, port2: processorPort } = new MessageChannel()
        workletNode.port.postMessage({ type: 'init', stream: stream, port: processorPort }, [processorPort])
        workletNode.port.onmessage = (ev: MessageEvent<any>) => {
            switch (ev.data.type) {
                case 'stream_stats':
                    this.handleStreamStats(streamUuid, ev.data.payload)
                    break
            }
        }

        this.receiverWorker.postMessage(
            {
                type: 'subscribe',
                payload: {
                    stream: stream,
                    processorPort: workerPort,
                },
            },
            [workerPort]
        )

        const remoteAudio: AudioEngineRemoteAudio = {
            fromTopology: 'ae',
            track: new FakeMediaStreamTrack({
                id: info.audioEngineStreamUuid ?? '',
                label: info.label,
                channelSelection: info.channelSelection,
                channelUnpackingMap: info.channelUnpackingMap,
                streamSettingsChangedHandler: ({ isMuted, volume }) => {
                    if (this.audioContext.state !== 'running') {
                        this.audioContext.resume()
                    }
                    if (volume) {
                        const volumes = volume.map((v) => v / 2000)
                        workletNode.port.postMessage({ type: 'volumes', volumes })
                    }
                    if (isMuted) {
                        workletNode.port.postMessage({ type: 'mutes', mutes: isMuted })
                    }
                },
            }),
            info: info,
        }

        this.remoteStreams.set(streamUuid, remoteAudio)
        this.streamNodes.set(streamUuid, makeStreamGraph(workletNode))

        return remoteAudio
    }

    async removeRemoteStream(streamUuid: string): Promise<boolean> {
        log.verbose('WebAudioEngineRoom/removeRemoteStream', `Unsubscribing from stream ${streamUuid}`)
        this.receiverWorker.postMessage({ type: 'unsubscribe', payload: { streamId: streamUuid } })
        const graph = this.streamNodes.get(streamUuid)
        if (graph?.patchDestNode) {
            this.removeAudioPatch(streamUuid)
        }
        if (graph) {
            graph.workletNode.port.postMessage({ type: 'close' })
            graph.workletNode.disconnect()
            this.streamNodes.delete(streamUuid)
        }

        this.remoteStreams.delete(streamUuid)

        return true
    }

    public startRemoteRecording(
        streamId: string,
        channelGroup: number | null,
        outputFileFullPath: string | null,
        source: AudioSourceInfo | null,
        settings: AudioOutputSettings | null
    ): void {
        if (!this.receiverWorker) {
            return
        }

        if (source && settings && this.audioContext && this.streamNodes.has(streamId)) {
            const graph = this.streamNodes.get(streamId)!
            graph.recordingDestNode = this.audioContext.createMediaStreamDestination()
            graph.workletNode.connect(graph.recordingDestNode)
            graph.recordingContext = new AudioContext({
                // @ts-ignore
                sinkId: source.deviceId,
            })
            graph.recordingSourceNode = graph.recordingContext.createMediaStreamSource(graph.recordingDestNode.stream)
            graph.recordingSourceNode.connect(graph.recordingContext.destination)
        }

        if (outputFileFullPath) {
            const recordingPort = ElectronIPC.openProAudioFileRecordingPort()
            this.receiverWorker.postMessage(
                {
                    type: 'start-recording',
                    payload: {
                        streamId,
                        outputFilePath: outputFileFullPath,
                        recordingPort,
                        channelGroup,
                    },
                },
                [recordingPort]
            )
        }
    }

    public stopRemoteRecording(streamId: string, channelGroup: number) {
        if (this.receiverWorker) {
            this.receiverWorker.postMessage({ type: 'stop-recording', payload: { streamId, channelGroup } })
        }

        if (this.streamNodes.has(streamId)) {
            const graph = this.streamNodes.get(streamId)!
            if (graph.recordingDestNode) {
                graph.recordingDestNode.disconnect()
                graph.recordingDestNode = undefined
            }
            if (graph.recordingSourceNode) {
                graph.recordingSourceNode.disconnect()
                graph.recordingSourceNode = undefined
            }
            if (graph.recordingContext) {
                graph.recordingContext.close()
                graph.recordingContext = undefined
            }
        }
    }

    createAudioPatch(sourceStreamId: string, targetDeviceId: string, targetChannel: number): void {
        const graph = this.streamNodes.get(sourceStreamId)
        if (!graph) {
            log.warning('WebAudioEngineRoom/createAudioPatch', `No graph found for stream ${sourceStreamId}`)
            return
        }

        const stream = this.remoteStreams.get(sourceStreamId)

        graph.patchDestNode = this.audioContext.createMediaStreamDestination()
        graph.patchDestNode.channelCount = stream?.info.channelCount ?? 1
        graph.patchDestNode.channelInterpretation = 'discrete'

        graph.workletNode.connect(graph.patchDestNode)

        stream?.info.channelCount
        patcher.addPatch({ stream: graph.patchDestNode, sourceStreamId, targetDeviceId, targetChannel })
    }

    removeAudioPatch(sourceStreamId: string): void {
        const graph = this.streamNodes.get(sourceStreamId)
        if (!graph) {
            log.warning('WebAudioEngineRoom/createAudioPatch', `No graph found for stream ${sourceStreamId}`)
            return
        }

        patcher.removePatch(sourceStreamId)
        if (graph.patchDestNode) {
            graph.patchDestNode = undefined
        }
    }

    removeAllAudioPatches(): void {
        for (const [streamId, graph] of this.streamNodes) {
            if (graph.patchDestNode) {
                this.removeAudioPatch(streamId)
            }
        }
    }

    listAudioPatches(): AudioPatch[] {
        let patches: AudioPatch[] = []
        for (const [_streamId, p] of patcher.patches) {
            patches.push({
                sourceStreamId: p.sourceStreamId,
                targetDeviceId: p.targetDeviceId,
                targetChannel: p.targetChannel,
            })
        }
        return patches
    }

    async destroy(): Promise<void> {
        await this.exitRoom()
        for (const [_streamId, g] of this.streamNodes) {
            g.workletNode.port.postMessage({ type: 'close' })
            g.workletNode.disconnect()
        }
        this.receiverWorker.removeEventListener('message', this.receiverMessageHandler)
        this.remoteStreams.clear()
        this.streamNodes.clear()
    }

    private handleStreamStats(streamId: string, stats: StreamStats) {
        const receiverStats = this.currentReceiverStats.get(streamId)
        const remoteStreamStats: AudioEngineRemoteStreamStats = {
            volume: stats.channelAmplitudes,
            bufferHeadMs: stats.bufferHeadMs,
            bufferWaterLineMs: stats.bufferWaterLineMs,
            bufferOverflowLineMs: stats.bufferOverflowLineMs,
            totalOverflowCt: stats.totalOverflowCt,
            totalUnderflowCt: stats.totalUnderflowCt,
            recentOverflowCt: stats.recentOverflowCt,
            recentUnderflowCt: stats.recentUnderflowCt,
            hardOverflowCt: stats.hardOverflowsCt,
            recvPacketRetryLateCt: 0,
            recvPacketRetryLostCt: 0,
            recvPacketRetryReqdCt: 0,
            recvPacketRetrySuccessCt: 0,
            pctLostAudio3s: stats.pctLostAudio3s * 100,
            connectionState: this.currentConnectionState,
            duplicatePacketCt: receiverStats?.duplicatePacketCt ?? 0,
            outOfOrderPacketCt: receiverStats?.outOfOrderPacketCt ?? 0,
            latePacketCt: stats.latePacketCt,
        }

        const lastStats = this.streamHealthStats.get(streamId)!
        const changed =
            lastStats &&
            Date.now() - lastStats.timestamp > 5_000 &&
            (lastStats.latePacketCt !== stats.latePacketCt ||
                lastStats.outOfOrderPacketCt !== remoteStreamStats.outOfOrderPacketCt ||
                lastStats.duplicatePacketCt !== remoteStreamStats.duplicatePacketCt ||
                lastStats.totalOverflowCt !== stats.totalOverflowCt ||
                lastStats.totalUnderflowCt !== stats.totalUnderflowCt)
        if (!lastStats || changed) {
            const healthStats: StreamHealthStats = {
                streamId,
                latePacketCt: stats.latePacketCt,
                outOfOrderPacketCt: remoteStreamStats.outOfOrderPacketCt,
                duplicatePacketCt: remoteStreamStats.duplicatePacketCt,
                totalOverflowCt: stats.totalOverflowCt,
                totalUnderflowCt: stats.totalUnderflowCt,
                timestamp: Date.now(),
            }
            this.streamHealthStats.set(streamId, healthStats)
        }
        if (changed) {
            log.isVerbose() &&
                log.verbose(`WebAudioEngine/${streamId}`, 'health stats', this.streamHealthStats.get(streamId))
        }

        if (stats.logs) {
            for (const v of stats.logs) {
                switch (v.level) {
                    case 'verbose':
                        log.isVerbose() && log.verbose(`AudioProcessor/${streamId}`, v.msg, v.vars)
                        break
                    case 'info':
                        log.info(`AudioProcessor/${streamId}`, v.msg, v.vars)
                        break
                    case 'warn':
                        log.warning(`AudioProcessor/${streamId}`, v.msg, v.vars)
                        break
                    case 'error':
                        log.error(`AudioProcessor/${streamId}`, v.msg, v.vars)
                        break
                }
            }
        }

        const stream = this.remoteStreams.get(streamId) || null
        const track = stream?.track as unknown as FakeMediaStreamTrack
        if (track instanceof FakeMediaStreamTrack) {
            track.internalSetCurrentRemoteStats(remoteStreamStats)
        }
    }
}
